import React from "react";
import styles from './LayoutDetail.module.css';
import { useParams, useNavigate } from "react-router-dom";
import { getLayoutById, updateLayout, deleteLayout } from '../../api/layoutApi';
import { getDevicesByLayout, updateDeviceInLayout, addDeviceToLayout, removeDeviceFromLayout } from '../../api/deviceApi';
import { getLinesByLayout, createLine, updateLine, deleteLine, setAllLinesToBlinking, updateLineStatus } from '../../api/lineApi';
import MapView from "../MapView/MapView";
import DeviceEdit from "../DeviceEdit/DeviceEdit";
import LayoutEdit from "../LayoutEdit/LayoutEdit";
import LineForm from "../LineCard/LineForm";
import LayoutHeader from "./components/LayoutHeader";
import ContentSection from "./components/ContentSection";

const LayoutDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [layout, setLayout] = React.useState(null);
    const [devices, setDevices] = React.useState([]);
    const [lines, setLines] = React.useState([]);
    const [selectedDevice, setSelectedDevice] = React.useState(null);
    const [typeFilter, setTypeFilter] = React.useState('all');
    const [zoom, setZoom] = React.useState(1);
    const [pan, setPan] = React.useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = React.useState(false);
    const [dragStart, setDragStart] = React.useState({ x: 0, y: 0 });
    const [editingDevice, setEditingDevice] = React.useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
    const [mousePosition, setMousePosition] = React.useState(null);
    const [clickedPosition, setClickedPosition] = React.useState(null);
    const [isApiLoading, setIsApiLoading] = React.useState(false);
    const [isEditLayoutModalOpen, setIsEditLayoutModalOpen] = React.useState(false);
    const [originalPositions, setOriginalPositions] = React.useState({});
    const [editingLine, setEditingLine] = React.useState(null);
    const [lineSelected, setLineSelected] = React.useState(null);
    const [isLineFormOpen, setIsLineFormOpen] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState('detail');
    const [loading, setLoading] = React.useState(false);
    const imageContainerRef = React.useRef(null);

    React.useEffect(() => {
        let intervalId;
        const fetchLayout = async () => {
            const res = await getLayoutById(id);
            setLayout(res);
        };
        const fetchDevices = async () => {
            const res = await getDevicesByLayout(id);
            setDevices(res.devices || []);
        };
        const fetchLines = async () => {
            try {
                const res = await getLinesByLayout(id);
                setLines(res);
                console.log('Res:', res);
                
            } catch (error) {
                console.error('Error fetching lines:', error);
            }
        };

        fetchLayout();
        fetchDevices();
        fetchLines();
    
        intervalId = setInterval(() => {
            fetchDevices();
            // fetchLines();
        }, 1000); // Fetch every 1 seconds
        return () => clearInterval(intervalId);
    }, [id]);

    const handleImageLoad = (e) => {
        // setImageSize({
        //     width: e.target.naturalWidth,
        //     height: e.target.naturalHeight
        // });
        // Reset zoom and pan when new image loads
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const handleDeviceSelect = (device) => {
        setSelectedDevice(device);
        setLineSelected(null);
        // Scroll to device in list if needed
        const deviceElement = document.getElementById(`device-${device._id}`);
        if (deviceElement) {
            deviceElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleLineSelect = (line) => {
        setLineSelected(line);
        setSelectedDevice(null);
        // Scroll to line in list if needed
        const lineElement = document.getElementById(`line-${line._id}`);
        if (lineElement) {
            lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    };

    const handleBackToList = () => {
        navigate('/');
    };

    // Zoom functionality
    const handleZoomIn = () => {
        setZoom(prevZoom => Math.min(prevZoom * 1.2, 5)); // Limit max zoom to 5x
    };

    const handleZoomOut = () => {
        setZoom(prevZoom => Math.max(prevZoom / 1.2, 0.5)); // Limit min zoom to 0.5x
    };

    const handleZoomReset = () => {
        setZoom(1);
        setPan({ x: 0, y: 0 });
    };

    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? 0.9 : 1.1;
        setZoom(prevZoom => Math.min(Math.max(prevZoom * delta, 0.5), 5)); // Limit zoom between 0.5x and 5x
    };

    const handleMouseDown = (e) => {
        if (zoom > 1) {
            setIsDragging(true);
            setDragStart({
                x: e.clientX - pan.x,
                y: e.clientY - pan.y
            });
        }
    };

    const handleMouseMove = (e) => {
        if (isDragging && zoom > 1) {
            setPan({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y
            });
        }
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
    }, [isDragging, dragStart, zoom]);

    // Device editing functionality
    const handleEditDevice = (device) => {
        setEditingDevice(device);
        setIsEditModalOpen(true);
    };

    const handleAddDevice = () => {
        // Create a new device template with clicked position or mouse position
        const defaultPosition = clickedPosition || mousePosition || { x: 100, y: 100 };
        setEditingDevice({
            name: '',
            type: 'signal',
            position: defaultPosition,
            status: 'off',
            layoutId: layout._id
        });
        setIsEditModalOpen(true);
    };

    const handleMapClick = (e) => {
        // If not dragging and clicking on empty area, save position for new device
        if (!isDragging && e.target.tagName === 'IMG') {
            setClickedPosition(mousePosition);
        }
    };

    const handleUpdateDevice = async (deviceId, updatedData) => {
        try {
            const updatedDevice = await updateDeviceInLayout(deviceId, updatedData);
            // Update layout state with the new device data
            setDevices(prev => prev.map(d => (d._id === deviceId ? updatedDevice : d)));
            return updatedDevice;
        } catch (error) {
            console.error('Error updating device:', error);
            throw error;
        }
    };

    const handleSaveDevice = async (deviceData) => {
        setIsApiLoading(true);
        try {
            // Ensure position data is valid
            const sanitizedDeviceData = {
                ...deviceData,
                position: {
                    x: deviceData.position?.x || 0,
                    y: deviceData.position?.y || 0
                }
            };
            
            let savedDevice;
            
            if (deviceData._id) {
                // Update existing device
                console.log('Updating device:', sanitizedDeviceData);
                savedDevice = await handleUpdateDevice(deviceData._id, sanitizedDeviceData);
            } else {
                // Add new device
                console.log('Adding new device:', sanitizedDeviceData);
                savedDevice = await addDeviceToLayout(sanitizedDeviceData);
                
                // Update local state with the response
                const deviceToAdd = savedDevice || {
                    ...sanitizedDeviceData,
                    _id: Date.now().toString(),
                    createdAt: new Date().toISOString()
                };
                setLayout(prev => ({
                    ...prev,
                    devices: [...(prev.devices || []), deviceToAdd._id]
                }));
                setDevices(prev => [...prev, deviceToAdd]);
                setEditingDevice(null);
            }
            
            // Clear clicked position after successful save
            setClickedPosition(null);
            
            console.log('Device saved successfully:', savedDevice);
        } catch (error) {
            console.error('Error saving device:', error);
            throw error; // Let DeviceEdit handle the error
        } finally {
            setIsApiLoading(false);
        }
    };

    const handleDeleteDevice = async (deviceId) => {
        setIsApiLoading(true);
        try {
            console.log('Deleting device:', deviceId);
            
            // Call API to delete device
            await removeDeviceFromLayout(deviceId);
            
            // Update local state after successful deletion
            setDevices(prev => (prev || []).filter(d => d._id !== deviceId));

            setLayout(prev => ({
                ...prev,
                devices: (prev?.devices || []).filter(id => id !== deviceId)
            }));
            
            // Clear selection if deleted device was selected
            if (selectedDevice?._id === deviceId) {
                setSelectedDevice(null);
            }
            
            console.log('Device deleted successfully');
        } catch (error) {
            console.error('Error deleting device:', error);
            throw error; // Let DeviceEdit handle the error
        } finally {
            setIsApiLoading(false);
        }
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setEditingDevice(null);
        setClickedPosition(null); // Clear clicked position when modal closes
    };

    const handleDevicePositionChange = async (deviceId, newPosition, saveToAPI = false) => {
        // If this is the start of a drag operation, save the original position
        if (!saveToAPI && !originalPositions[deviceId]) {
            const device = devices.find(d => d._id === deviceId);
            if (device) {
                setOriginalPositions(prev => ({
                    ...prev,
                    [deviceId]: { ...device.position }
                }));
            }
        }

        // Update local state immediately for visual feedback
        setDevices(prev => prev.map(d =>
            d._id === deviceId ? { ...d, position: newPosition } : d
        ));

        // If saveToAPI is true, call the API to persist the change
        if (saveToAPI) {
            try {
                setIsApiLoading(true);
                const device = devices.find(d => d._id === deviceId);
                if (device) {
                    const updatedDevice = await updateDeviceInLayout(deviceId, {
                        ...device,
                        position: newPosition
                    });
                    console.log('Device position saved successfully:', updatedDevice);
                    // Clear original position after successful save
                    setOriginalPositions(prev => {
                        const newPositions = { ...prev };
                        delete newPositions[deviceId];
                        return newPositions;
                    });
                }
            } catch (error) {
                console.error('Error saving device position:', error);
                // Revert to original position on error
                const originalPosition = originalPositions[deviceId];
                if (originalPosition) {
                    setDevices(prev => prev.map(d =>
                        d._id === deviceId ? { ...d, position: originalPosition } : d
                    ));
                }
                throw error; // Re-throw for MapView to handle
            } finally {
                setIsApiLoading(false);
            }
        }
    };

    const handleCancelPositionChange = (deviceId) => {
        const originalPosition = originalPositions[deviceId];
        if (originalPosition) {
            setDevices(prev => prev.map(d =>
                d._id === deviceId ? { ...d, position: originalPosition } : d
            ));
            // Clear original position after cancel
            setOriginalPositions(prev => {
                const newPositions = { ...prev };
                delete newPositions[deviceId];
                return newPositions;
            });
        }
    };

    // Line management functionality
    const handleCreateLine = async (lineData) => {
        setIsApiLoading(true);
        try {
            await createLine(lineData);
            // Refresh lines
            const res = await getLinesByLayout(id);
            setLines(res);
            setIsLineFormOpen(false);
            setEditingLine(null);
        } catch (error) {
            console.error('Error creating line:', error);
            throw error;
        } finally {
            setIsApiLoading(false);
        }
    };

    const handleUpdateLine = async (lineData) => {
        setIsApiLoading(true);
        try {
            await updateLine(editingLine._id, lineData);
            // Refresh lines
            const res = await getLinesByLayout(id);
            setLines(res);
            // Close form after update
            setIsLineFormOpen(false);
            setEditingLine(null);
        } catch (error) {
            console.error('Error updating line:', error);
            throw error;
        } finally {
            setIsApiLoading(false);
        }
    };

    const handleDeleteLine = async (line) => {
        if (window.confirm('Are you sure you want to delete this line? This action cannot be undone.')) {
            setIsApiLoading(true);
            setEditingLine(line);
            try {
                await deleteLine(line._id);
                // Refresh lines
                const res = await getLinesByLayout(id);
                setLines(res);
                setEditingLine(null);
            } catch (error) {
                console.error('Error deleting line:', error);
                alert('Failed to delete line. Please try again.');
            } finally {
                setIsApiLoading(false);
            }
        }
    };

    const handleEditLine = (line) => {
        setEditingLine(line);
        setIsLineFormOpen(true);
    };

    const handleAddLine = () => {
        setEditingLine(null);
        setIsLineFormOpen(true);
    };

    const handleSetAllLinesBlinking = async () => {
        setIsApiLoading(true);
        try {
            await setAllLinesToBlinking(id);
            // Refresh lines
            const res = await getLinesByLayout(id);
            setLines(res);
        } catch (error) {
            console.error('Error setting lines to blinking:', error);
            alert('Failed to set lines to blinking. Please try again.');
        } finally {
            setIsApiLoading(false);
        }
    };

    const handleSignalChange = async (lineId, signal) => {
        setLoading(true);
        try {
            const line = lines.find(l => l._id === lineId);
            if (line) {
                const newStatus = { ...line.status, signal };
                console.log('Updating line signal:', newStatus);
                await updateLineStatus(lineId, { status: newStatus });
                // Refresh lines
                const res = await getLinesByLayout(id);
                setLines(res);
            }
        } catch (error) {
            console.error('Error updating signal:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleLineSubmit = async (lineData) => {
        if (lineData._refresh) {
            // Just refresh data without updating
            const res = await getLinesByLayout(id);
            setLines(res);
            setIsLineFormOpen(false);
            setEditingLine(null);
        } else if (editingLine) {
            await handleUpdateLine(lineData);
        } else {
            await handleCreateLine(lineData);
        }
    };

    if (!layout) {
        return <div className={styles.loading}>⚡ Loading layout...</div>;
    }

    return (
        <div className={styles.layoutDetail}>
            <LayoutHeader
                layout={layout}
                selectedDevice={selectedDevice}
                onBackToList={handleBackToList}
                onClearSelection={() => {setSelectedDevice(null); setLineSelected(null);}}
                onEditLayout={() => setIsEditLayoutModalOpen(true)}
                onDeleteLayout={() => {
                    if (window.confirm('Are you sure you want to delete this layout? This action cannot be undone.')) {
                        setIsApiLoading(true);
                        deleteLayout(layout._id)
                            .then(() => {
                                navigate('/');
                            })
                            .catch(error => {
                                console.error('Error deleting layout:', error);
                                alert('Failed to delete layout. Please try again.');
                            })
                            .finally(() => {
                                setIsApiLoading(false);
                            });
                    }
                }}
                onAddDevice={handleAddDevice}
            />

            {layout.description && (
                <p className={styles.description}>{layout.description}</p>
            )}

            {/* Main Content - Map and Devices */}
            <div className={styles.mainContent}>
                {/* Left Side - Map View */}
                <MapView
                    layout={layout}
                    devices={devices}
                    selectedDevice={selectedDevice}
                    selectedLine={lineSelected}
                    onDeviceSelect={handleDeviceSelect}
                    zoom={zoom}
                    pan={pan}
                    isDragging={isDragging}
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onZoomReset={handleZoomReset}
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onImageLoad={handleImageLoad}
                    imageContainerRef={imageContainerRef}
                    onMousePositionChange={setMousePosition}
                    mousePosition={mousePosition}
                    onMapClick={handleMapClick}
                    clickedPosition={clickedPosition}
                    filter={typeFilter}
                    onDevicePositionChange={handleDevicePositionChange}
                    onCancelPositionChange={handleCancelPositionChange}
                />

                {/* Right Side - Content Section */}
                <ContentSection
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                    devices={devices}
                    typeFilter={typeFilter}
                    selectedDevice={selectedDevice}
                    selectedLine={lineSelected}
                    lines={lines}
                    setLines={setLines}
                    loading={loading}
                    layoutId={id}
                    onTypeFilterChange={setTypeFilter}
                    onDeviceSelect={handleDeviceSelect}
                    onLineSelect={handleLineSelect}
                    onDeviceEdit={handleEditDevice}
                    onDeviceDelete={handleDeleteDevice}
                    onUpdateDevice={handleUpdateDevice}
                    onAddDevice={handleAddDevice}
                    onAddLine={handleAddLine}
                    onEditLine={handleEditLine}
                    onDeleteLine={handleDeleteLine}
                    onSignalChange={handleSignalChange}
                    onAllBlinking={handleSetAllLinesBlinking}
                />
            </div>
                                    


            {/* Device Edit Modal */}
            <DeviceEdit
                layout={layout}
                device={editingDevice}
                isOpen={isEditModalOpen}
                onClose={handleCloseEditModal}
                onSave={handleSaveDevice}
                onDelete={handleDeleteDevice}
            />

            {/* Line Form Modal */}
            <LineForm
                open={isLineFormOpen}
                onClose={() => {
                    setIsLineFormOpen(false);
                    setEditingLine(null);
                }}
                onSubmit={handleLineSubmit}
                line={editingLine}
                layoutId={id}
            />

            {/* API Loading Overlay */}
            {isApiLoading && (
                <div className={styles.apiLoadingOverlay}>
                    <div className={styles.apiLoadingContent}>
                        <div className={styles.spinner}></div>
                        <p>Processing...</p>
                    </div>
                </div>
            )}

            {/* Layout Edit Modal */}
            <LayoutEdit
                layout={layout}
                isOpen={isEditLayoutModalOpen}
                onClose={() => setIsEditLayoutModalOpen(false)}
                onSave={async (layoutData) => {
                    setIsApiLoading(true);
                    try {
                        const updatedLayout = await updateLayout(layout._id, layoutData);
                        setLayout(updatedLayout);
                        setIsEditLayoutModalOpen(false);
                    } catch (error) {
                        console.error('Error updating layout:', error);
                        throw error;
                    } finally {
                        setIsApiLoading(false);
                    }
                }}
            />
        </div>
    );
}

export default LayoutDetail;